// drchronoTemplateDebug.tsx
import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  FlatList,
  ScrollView,
  Modal,
} from "react-native";
import {
  getDrChronoTemplates,
  getDrChronoTemplateFields,
} from "../../../../../services/api-integrations-drchrono";
import {
  startDrChronoTemplateConversion,
  getDrChronoTemplateConversionStatus,
  pollDrChronoTemplateConversion,
  BackgroundTaskStatus,
  type TBackgroundTaskStatusResponse,
} from "../../../../../services/api-integrations-drchrono"; // Your new functions
import type { TSession } from "../../../../../utils/types";

// Interfaces
export interface TemplateDebugResult {
  section: string;
  title: string;
  data: any;
  success: boolean;
  error?: string;
}

export interface DrChronoTemplate {
  id: number;
  name: string;
  doctor: string;
  archived: boolean;
  is_onpatient: boolean;
  is_persistent: boolean;
  updated_at: string;
}

export interface DrChronoTemplateField {
  field_id: number;
  field_name: string;
  data_type: string;
  required: boolean;
  allowed_values?: string[];
  comment?: string;
  archived: boolean;
  value_instructions: string;
  how_to_set_value: string;
}

// Conversion task state interface
interface ConversionTask {
  taskId: number;
  status: BackgroundTaskStatus;
  progress: string;
  startTime: Date;
  templateIds: number[];
  result?: any;
  error?: string;
}

// Class for template operations
export class DrChronoTemplateExplorer {
  private session: TSession;

  constructor(session: TSession) {
    this.session = session;
  }

  // Get all available templates
  async getAllTemplates(): Promise<{
    success: boolean;
    templates: DrChronoTemplate[];
    error?: string;
  }> {
    try {
      console.log("📋 Fetching all Dr. Chrono templates...");
      const response = await getDrChronoTemplates(this.session);

      return {
        success: true,
        templates: response.templates,
      };
    } catch (error: any) {
      console.error("Error fetching templates:", error);
      return {
        success: false,
        templates: [],
        error: error.message,
      };
    }
  }

  // Get template fields by template ID
  async getTemplateFields(templateId: number): Promise<{
    success: boolean;
    templateName?: string;
    fields: DrChronoTemplateField[];
    fieldCount: number;
    error?: string;
  }> {
    try {
      console.log(`🔧 Fetching fields for template ${templateId}...`);
      const response = await getDrChronoTemplateFields(
        this.session,
        templateId,
      );

      return {
        success: true,
        templateName: response.template_name,
        fields: response.editable_fields,
        fieldCount: response.field_count,
      };
    } catch (error: any) {
      console.error(`Error fetching fields for template ${templateId}:`, error);
      return {
        success: false,
        fields: [],
        fieldCount: 0,
        error: error.message,
      };
    }
  }

  // Get templates with a doctor filter
  async getTemplatesForDoctor(doctorId: number): Promise<{
    success: boolean;
    templates: DrChronoTemplate[];
    error?: string;
  }> {
    try {
      console.log(`👨‍⚕️ Fetching templates for doctor ${doctorId}...`);
      const response = await getDrChronoTemplates(this.session, doctorId);

      return {
        success: true,
        templates: response.templates,
      };
    } catch (error: any) {
      console.error(`Error fetching templates for doctor ${doctorId}:`, error);
      return {
        success: false,
        templates: [],
        error: error.message,
      };
    }
  }

  // Analyze field types for a template
  analyzeFieldTypes(fields: DrChronoTemplateField[]): {
    fieldTypes: Record<string, number>;
    totalFields: number;
    requiredFields: number;
    textFields: number;
    selectFields: number;
  } {
    const fieldTypes: Record<string, number> = {};
    let requiredFields = 0;
    let textFields = 0;
    let selectFields = 0;

    fields.forEach((field) => {
      const dataType = field.data_type || "Unknown";
      fieldTypes[dataType] = (fieldTypes[dataType] || 0) + 1;

      if (field.required) requiredFields++;

      if (field.data_type === "String" && !field.allowed_values?.length) {
        textFields++;
      }

      if (field.allowed_values?.length) {
        selectFields++;
      }
    });

    return {
      fieldTypes,
      totalFields: fields.length,
      requiredFields,
      textFields,
      selectFields,
    };
  }

  // Get unique doctors from templates
  getUniqueDoctors(
    templates: DrChronoTemplate[],
  ): Array<{ id: string; name: string }> {
    const doctorMap = new Map<string, string>();

    templates.forEach((template) => {
      if (template.doctor && !doctorMap.has(template.doctor)) {
        doctorMap.set(template.doctor, `Doctor ${template.doctor}`);
      }
    });

    return Array.from(doctorMap.entries()).map(([id, name]) => ({ id, name }));
  }

  // Format field for display
  formatFieldForDisplay(field: DrChronoTemplateField): {
    id: number;
    name: string;
    type: string;
    required: boolean;
    instructions: string;
    options?: string[];
    comment?: string;
  } {
    return {
      id: field.field_id,
      name: field.field_name,
      type: field.data_type,
      required: field.required,
      instructions: field.value_instructions,
      options: field.allowed_values?.length ? field.allowed_values : undefined,
      comment: field.comment || undefined,
    };
  }
}

// React component that uses the DrChronoTemplateExplorer class
const DrChronoTemplateExplorerComponent: React.FC<{ session: TSession }> = ({
  session,
}) => {
  const [explorer] = useState(new DrChronoTemplateExplorer(session));
  const [templates, setTemplates] = useState<DrChronoTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<DrChronoTemplate | null>(null);
  const [templateFields, setTemplateFields] = useState<DrChronoTemplateField[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState({
    templates: false,
    fields: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>([]);

  // New state for conversion task
  const [conversionTask, setConversionTask] = useState<ConversionTask | null>(
    null,
  );
  const [showResultModal, setShowResultModal] = useState(false);
  const [conversionResult, setConversionResult] = useState<any>(null);

  // Load templates on component mount
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setIsLoading((prev) => ({ ...prev, templates: true }));
    setError(null);

    try {
      const result = await explorer.getAllTemplates();
      if (result.success) {
        setTemplates(result.templates);
      } else {
        setError(result.error || "Failed to load templates");
      }
    } catch (error: any) {
      setError(error.message || "Failed to load templates");
    } finally {
      setIsLoading((prev) => ({ ...prev, templates: false }));
    }
  };

  const toggleTemplateSelection = (templateId: number) => {
    setSelectedTemplateIds((prev) =>
      prev.includes(templateId)
        ? prev.filter((id) => id !== templateId)
        : [...prev, templateId],
    );
  };

  const convertTemplates = async () => {
    if (selectedTemplateIds.length === 0) return;

    try {
      setError(null);

      // Start the conversion
      console.log("🚀 Starting template conversion...");
      const startResponse = await startDrChronoTemplateConversion(
        session,
        selectedTemplateIds,
      );

      // Initialize task state
      setConversionTask({
        taskId: startResponse.task_id,
        status: BackgroundTaskStatus.PENDING,
        progress: "Starting conversion...",
        startTime: new Date(),
        templateIds: selectedTemplateIds,
      });

      // Poll for completion
      console.log("⏳ Polling for completion...");
      const finalResult = await pollDrChronoTemplateConversion(
        session,
        startResponse.task_id,
        {
          pollIntervalMs: 2000, // Poll every 2 seconds
          maxAttempts: 150, // 5 minutes max
          onProgress: (status: TBackgroundTaskStatusResponse) => {
            console.log("📊 Progress update:", status.status);

            setConversionTask((prev) =>
              prev
                ? {
                    ...prev,
                    status: status.status,
                    progress: getProgressMessage(status.status),
                    result: status.result,
                    error: status.result?.error,
                  }
                : null,
            );
          },
        },
      );

      // Handle completion
      if (finalResult.status === BackgroundTaskStatus.COMPLETED) {
        console.log("✅ Conversion completed successfully!");
        setConversionResult(finalResult.result);
        setShowResultModal(true);

        setConversionTask((prev) =>
          prev
            ? {
                ...prev,
                status: BackgroundTaskStatus.COMPLETED,
                progress: "Conversion completed successfully!",
                result: finalResult.result,
              }
            : null,
        );
      } else {
        throw new Error(finalResult.result?.error || "Conversion failed");
      }
    } catch (error: any) {
      console.error("❌ Conversion failed:", error);
      setError(error.message);

      setConversionTask((prev) =>
        prev
          ? {
              ...prev,
              status: BackgroundTaskStatus.FAILED,
              progress: "Conversion failed",
              error: error.message,
            }
          : null,
      );
    }
  };

  const getProgressMessage = (status: BackgroundTaskStatus): string => {
    switch (status) {
      case BackgroundTaskStatus.PENDING:
        return "Queued for processing...";
      case BackgroundTaskStatus.IN_PROGRESS:
        return "Converting templates to SOAP format...";
      case BackgroundTaskStatus.COMPLETED:
        return "Conversion completed successfully!";
      case BackgroundTaskStatus.FAILED:
        return "Conversion failed";
      default:
        return "Unknown status";
    }
  };

  const getElapsedTime = (startTime: Date): string => {
    const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
    return elapsed < 60
      ? `${elapsed}s`
      : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
  };

  const loadTemplateFields = async (template: DrChronoTemplate) => {
    setIsLoading((prev) => ({ ...prev, fields: true }));
    setSelectedTemplate(template);
    setTemplateFields([]);
    setError(null);

    try {
      const result = await explorer.getTemplateFields(template.id);
      if (result.success) {
        setTemplateFields(result.fields);
      } else {
        setError(result.error || "Failed to load template fields");
      }
    } catch (error: any) {
      setError(error.message || "Failed to load template fields");
    } finally {
      setIsLoading((prev) => ({ ...prev, fields: false }));
    }
  };

  const renderTemplateItem = ({ item }: { item: DrChronoTemplate }) => (
    <View style={styles.templateItemContainer}>
      {/* Checkbox for multi-select */}
      <TouchableOpacity
        style={[
          styles.checkbox,
          selectedTemplateIds.includes(item.id) && styles.checkboxSelected,
        ]}
        onPress={() => toggleTemplateSelection(item.id)}
        disabled={conversionTask?.status === BackgroundTaskStatus.IN_PROGRESS}
      >
        {selectedTemplateIds.includes(item.id) && (
          <Text style={styles.checkmark}>✓</Text>
        )}
      </TouchableOpacity>

      {/* Template details */}
      <TouchableOpacity
        style={[
          styles.templateItem,
          selectedTemplate?.id === item.id && styles.selectedTemplateItem,
        ]}
        onPress={() => loadTemplateFields(item)}
        disabled={isLoading.fields}
      >
        <Text style={styles.templateName}>{item.name}</Text>
        <Text style={styles.templateMeta}>
          Doctor: {item.doctor} | {item.archived ? "Archived" : "Active"}
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderFieldItem = ({ item }: { item: DrChronoTemplateField }) => (
    <View style={styles.fieldItem}>
      <View style={styles.fieldHeader}>
        <Text style={styles.fieldName}>{item.field_name}</Text>
        <View style={styles.fieldBadges}>
          <Text style={styles.fieldTypeBadge}>{item.data_type}</Text>
          {item.required && <Text style={styles.requiredBadge}>Required</Text>}
        </View>
      </View>
      <Text style={styles.fieldInstructions}>{item.value_instructions}</Text>
      {item.allowed_values && item.allowed_values.length > 0 && (
        <Text style={styles.fieldOptions}>
          Options: {item.allowed_values.join(", ")}
        </Text>
      )}
    </View>
  );

  const renderConversionStatus = () => {
    if (!conversionTask) return null;

    const statusColor = {
      [BackgroundTaskStatus.PENDING]: "#ffa726",
      [BackgroundTaskStatus.IN_PROGRESS]: "#42a5f5",
      [BackgroundTaskStatus.COMPLETED]: "#66bb6a",
      [BackgroundTaskStatus.FAILED]: "#ef5350",
    }[conversionTask.status];

    return (
      <View style={[styles.conversionStatus, { borderLeftColor: statusColor }]}>
        <View style={styles.statusHeader}>
          <Text style={styles.statusTitle}>
            🔄 Template Conversion (Task #{conversionTask.taskId})
          </Text>
          <Text style={styles.statusTime}>
            {getElapsedTime(conversionTask.startTime)}
          </Text>
        </View>

        <Text style={[styles.statusText, { color: statusColor }]}>
          Status: {conversionTask.status.toUpperCase()}
        </Text>
        <Text style={styles.progressText}>{conversionTask.progress}</Text>

        <Text style={styles.templateCount}>
          Converting {conversionTask.templateIds.length} template(s):{" "}
          {conversionTask.templateIds.join(", ")}
        </Text>

        {conversionTask.error && (
          <Text style={styles.errorText}>Error: {conversionTask.error}</Text>
        )}

        {conversionTask.status === BackgroundTaskStatus.COMPLETED &&
          conversionTask.result && (
            <TouchableOpacity
              style={styles.viewResultButton}
              onPress={() => {
                setConversionResult(conversionTask.result);
                setShowResultModal(true);
              }}
            >
              <Text style={styles.viewResultButtonText}>
                📄 View SOAP Configuration
              </Text>
            </TouchableOpacity>
          )}
      </View>
    );
  };

  const renderResultModal = () => (
    <Modal
      visible={showResultModal}
      animationType="slide"
      transparent={false}
      onRequestClose={() => setShowResultModal(false)}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>🎉 SOAP Configuration Result</Text>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={() => setShowResultModal(false)}
          >
            <Text style={styles.closeButtonText}>✕</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalContent}>
          <View style={styles.jsonContainer}>
            <Text style={styles.jsonText}>
              {JSON.stringify(conversionResult, null, 2)}
            </Text>
          </View>
        </ScrollView>
      </View>
    </Modal>
  );

  return (
    <View style={styles.container}>
      <Text style={styles.title}>📋 Dr. Chrono Template Explorer</Text>

      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      {renderConversionStatus()}

      <View style={styles.content}>
        {/* Templates Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>
            Templates ({templates.length})
            {isLoading.templates && " - Loading..."}
          </Text>

          {isLoading.templates ? (
            <Text style={styles.loadingText}>Loading templates...</Text>
          ) : (
            <FlatList
              data={templates}
              renderItem={renderTemplateItem}
              keyExtractor={(item) => item.id.toString()}
              style={styles.templatesList}
              showsVerticalScrollIndicator={false}
            />
          )}
        </View>

        {selectedTemplateIds.length > 0 && (
          <TouchableOpacity
            style={[
              styles.convertButton,
              (conversionTask?.status === BackgroundTaskStatus.IN_PROGRESS ||
                conversionTask?.status === BackgroundTaskStatus.PENDING) &&
                styles.convertButtonDisabled,
            ]}
            onPress={convertTemplates}
            disabled={
              conversionTask?.status === BackgroundTaskStatus.IN_PROGRESS ||
              conversionTask?.status === BackgroundTaskStatus.PENDING
            }
          >
            <Text style={styles.convertButtonText}>
              {conversionTask?.status === BackgroundTaskStatus.IN_PROGRESS ||
              conversionTask?.status === BackgroundTaskStatus.PENDING
                ? "🔄 Converting..."
                : `🚀 Convert ${selectedTemplateIds.length} Template(s) to SOAP`}
            </Text>
          </TouchableOpacity>
        )}

        {/* Fields Section */}
        {selectedTemplate && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              Fields for "{selectedTemplate.name}"
              {isLoading.fields && " - Loading..."}
              {!isLoading.fields && ` (${templateFields.length})`}
            </Text>

            {isLoading.fields ? (
              <Text style={styles.loadingText}>Loading fields...</Text>
            ) : templateFields.length > 0 ? (
              <FlatList
                data={templateFields}
                renderItem={renderFieldItem}
                keyExtractor={(item) => item.field_id.toString()}
                style={styles.fieldsList}
                showsVerticalScrollIndicator={false}
              />
            ) : (
              <Text style={styles.emptyText}>No editable fields found</Text>
            )}
          </View>
        )}
      </View>

      {renderResultModal()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "white",
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    borderWidth: 1,
    borderColor: "#e9ecef",
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 16,
    color: "#333",
  },
  errorContainer: {
    backgroundColor: "#ffebee",
    padding: 12,
    borderRadius: 6,
    marginBottom: 16,
  },
  errorText: {
    color: "#c62828",
    fontSize: 14,
  },
  content: {
    gap: 20,
  },
  section: {
    flex: 1,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 12,
    color: "#333",
  },
  templatesList: {
    maxHeight: 300,
  },
  templateItem: {
    backgroundColor: "#f8f9fa",
    padding: 12,
    borderRadius: 6,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#e9ecef",
    flex: 1,
  },
  selectedTemplateItem: {
    borderColor: "#007AFF",
    backgroundColor: "#e3f2fd",
  },
  templateName: {
    fontSize: 14,
    fontWeight: "600",
    color: "#333",
    marginBottom: 4,
  },
  templateMeta: {
    fontSize: 12,
    color: "#666",
  },
  fieldsList: {
    maxHeight: 400,
  },
  fieldItem: {
    backgroundColor: "#f8f9fa",
    padding: 12,
    borderRadius: 6,
    marginBottom: 8,
    borderLeftWidth: 4,
    borderLeftColor: "#007AFF",
  },
  fieldHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  fieldName: {
    fontSize: 14,
    fontWeight: "600",
    color: "#333",
    flex: 1,
    marginRight: 8,
  },
  fieldBadges: {
    flexDirection: "row",
    gap: 4,
  },
  fieldTypeBadge: {
    fontSize: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: "#e3f2fd",
    color: "#1565c0",
  },
  requiredBadge: {
    fontSize: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: "#ffebee",
    color: "#c62828",
  },
  fieldInstructions: {
    fontSize: 12,
    color: "#666",
    marginBottom: 6,
  },
  fieldOptions: {
    fontSize: 12,
    color: "#666",
    fontStyle: "italic",
  },
  loadingText: {
    fontSize: 14,
    color: "#666",
    textAlign: "center",
    padding: 20,
  },
  emptyText: {
    fontSize: 14,
    color: "#999",
    textAlign: "center",
    padding: 20,
    fontStyle: "italic",
  },
  convertButton: {
    backgroundColor: "#007AFF",
    padding: 12,
    borderRadius: 6,
    marginTop: 12,
  },
  convertButtonDisabled: {
    backgroundColor: "#ccc",
  },
  convertButtonText: {
    color: "white",
    textAlign: "center",
    fontWeight: "600",
  },
  templateItemContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: "#007AFF",
    borderRadius: 4,
    marginRight: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  checkboxSelected: {
    backgroundColor: "#007AFF",
  },
  checkmark: {
    color: "white",
    fontSize: 16,
    fontWeight: "bold",
  },

  // New styles for conversion status
  conversionStatus: {
    backgroundColor: "#f8f9fa",
    padding: 16,
    borderRadius: 8,
    marginBottom: 20,
    borderLeftWidth: 4,
  },
  statusHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  statusTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#333",
  },
  statusTime: {
    fontSize: 12,
    color: "#666",
    fontFamily: "monospace",
  },
  statusText: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 4,
  },
  progressText: {
    fontSize: 14,
    color: "#666",
    marginBottom: 8,
  },
  templateCount: {
    fontSize: 12,
    color: "#666",
    fontStyle: "italic",
  },
  viewResultButton: {
    backgroundColor: "#4caf50",
    padding: 8,
    borderRadius: 4,
    marginTop: 8,
    alignSelf: "flex-start",
  },
  viewResultButtonText: {
    color: "white",
    fontSize: 12,
    fontWeight: "600",
  },

  // Modal styles
  modalContainer: {
    flex: 1,
    backgroundColor: "white",
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#e9ecef",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#333",
  },
  closeButton: {
    padding: 8,
  },
  closeButtonText: {
    fontSize: 20,
    color: "#666",
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  jsonContainer: {
    backgroundColor: "#f8f9fa",
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e9ecef",
  },
  jsonText: {
    fontSize: 12,
    fontFamily: "monospace",
    color: "#333",
    lineHeight: 18,
  },
});

// Export the React component as default
export default DrChronoTemplateExplorerComponent;
